import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { IconButton } from './Button';
import { trapTab } from './hooks';

/** Các hộp thoại đang mở (trên cùng ở cuối) — Esc ở ngoài hộp thoại chỉ đóng hộp trên cùng. */
const open: symbol[] = [];

/**
 * Hộp thoại (role dialog, aria-modal): giữ focus bên trong (Tab/Shift+Tab vòng lại), Esc hoặc bấm nền để đóng, trả
 * focus về phần tử trước đó khi đóng. `testId` gắn lên khung hộp thoại (test UI dựa vào).
 */
export function Modal({
  title,
  onClose,
  children,
  testId,
  size = 'md',
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
  /** `palette`: bảng tìm kiếm ⌘K — đặt gần đầu màn, rộng hơn. */
  size?: 'md' | 'lg' | 'palette';
}) {
  const box = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Focus ô nhập đầu tiên (nếu có), không thì chính hộp thoại.
    const el = box.current;
    (el?.querySelector<HTMLElement>('input:not([type="hidden"]):not([type="checkbox"]):not([disabled]), select, textarea') ?? el)?.focus();
    return () => previous?.focus();
  }, []);

  // Bấm một nút rồi nút bị tắt/biến mất làm focus rơi về <body>: Esc không còn đi qua hộp thoại → nghe cả ở document
  // (khi focus đang trong hộp thoại, `onKeyDown` bên dưới đã xử lý và dừng lan truyền nên không đóng hai lần).
  useEffect(() => {
    const me = Symbol('modal');
    open.push(me);
    const onDocKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && open[open.length - 1] === me) onClose();
    };
    document.addEventListener('keydown', onDocKey);
    return () => {
      document.removeEventListener('keydown', onDocKey);
      open.splice(open.indexOf(me), 1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    trapTab(e, box.current);
  }

  return (
    <div className={`ws-modal-backdrop ws-modal-backdrop-${size}`} onClick={onClose}>
      <div
        ref={box}
        className={`ws-modal ws-modal-${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={testId}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <header className="ws-modal-head">
          <h2 id={titleId}>{title}</h2>
          <IconButton label="Đóng" icon="close" onClick={onClose} />
        </header>
        <div className="ws-modal-body">{children}</div>
      </div>
    </div>
  );
}
